import {Trash2Icon} from "lucide-react";
import type {GroupDiscountTier, GroupDiscountType} from "../../../../../types/event";

// UI-string mirror of GroupDiscountTier. Numeric fields are held as strings while
// editing; `_key` is a client-side identity for stable list rendering.
export type TierForm = {
    _key: string;
    minQuantity: string;
    discountType: GroupDiscountType;
    discountValue: string;
};

export function newTier(): TierForm {
    return {_key: crypto.randomUUID(), minQuantity: "", discountType: "PERCENT", discountValue: ""};
}

// Map an API tier list (any-typed from the wire) into editable rows.
export function tiersFromApi(raw: unknown): TierForm[] {
    if (!Array.isArray(raw)) return [];
    return raw.map((t: any) => ({
        _key: crypto.randomUUID(),
        minQuantity: t?.minQuantity != null ? String(t.minQuantity) : "",
        discountType: (t?.discountType === "FIXED" ? "FIXED" : "PERCENT") as GroupDiscountType,
        discountValue: t?.discountValue != null ? String(t.discountValue) : "",
    }));
}

// Map editable rows back to the request shape, dropping incomplete rows. The backend
// validates ranges and duplicate minQuantity and returns 400 on failure.
export function tiersToApi(tiers: TierForm[]): GroupDiscountTier[] {
    return tiers
        .filter((r) => r.minQuantity.trim() !== "" && r.discountValue.trim() !== "")
        .map((r) => {
            const min = Math.max(1, Math.trunc(Number(r.minQuantity) || 1));
            const val = Number(r.discountValue);
            return {
                minQuantity: min,
                discountType: r.discountType,
                discountValue: Number.isFinite(val) ? val : 0,
            };
        });
}

type Props = {
    tiers: TierForm[];
    onChange: (next: TierForm[]) => void;
    currency?: string;
    disabled?: boolean;
};

export default function TierListEditor({tiers, onChange, currency = "NAD", disabled = false}: Props) {
    const update = (key: string, patch: Partial<TierForm>) =>
        onChange(tiers.map((t) => (t._key === key ? {...t, ...patch} : t)));

    const remove = (key: string) => onChange(tiers.filter((t) => t._key !== key));

    const add = () => onChange([...tiers, newTier()]);

    return (
        <div className="space-y-3">
            {tiers.length === 0 ? (
                <p className="text-sm text-gray-500">No tiers yet. Add one to offer quantity-based discounts.</p>
            ) : (
                tiers.map((row) => (
                    <div
                        key={row._key}
                        className="grid grid-cols-1 gap-3 rounded-lg border border-gray-200 p-3 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end"
                    >
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Min quantity</label>
                            <input
                                type="number"
                                min={1}
                                disabled={disabled}
                                value={row.minQuantity}
                                onChange={(e) => update(row._key, {minQuantity: e.target.value})}
                                placeholder="e.g. 10"
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none disabled:bg-gray-50"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Discount type</label>
                            <select
                                disabled={disabled}
                                value={row.discountType}
                                onChange={(e) => update(row._key, {discountType: e.target.value as GroupDiscountType})}
                                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none disabled:bg-gray-50"
                            >
                                <option value="PERCENT">Percentage (%)</option>
                                <option value="FIXED">Fixed value</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Discount value {row.discountType === "PERCENT" ? "(%)" : `(${currency})`}
                            </label>
                            <input
                                inputMode="decimal"
                                disabled={disabled}
                                value={row.discountValue}
                                onChange={(e) => update(row._key, {discountValue: e.target.value})}
                                placeholder={row.discountType === "PERCENT" ? "e.g. 5" : "e.g. 200"}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none disabled:bg-gray-50"
                            />
                        </div>
                        <button
                            type="button"
                            disabled={disabled}
                            onClick={() => remove(row._key)}
                            aria-label="Remove tier"
                            className="inline-flex h-10 items-center justify-center rounded-md border border-gray-300 px-3 text-gray-500 hover:bg-gray-50 hover:text-red-600 disabled:opacity-50"
                        >
                            <Trash2Icon className="size-4"/>
                        </button>
                    </div>
                ))
            )}

            <button
                type="button"
                disabled={disabled}
                onClick={add}
                className="rounded-md border border-dashed border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:border-orange-400 hover:text-orange-700 disabled:opacity-50"
            >
                + Add tier
            </button>
        </div>
    );
}
